import { useState, useEffect } from 'react';
import { GRADE_COLOR, HelpTip, Loading, PanelHowTo, api } from './_barrel.js';


        // Director network — the other companies this company's current directors
        // also sit on, with due-diligence red flags. Live Companies House lookup.
        // Auto-loaded on first open (officer × appointment fan-out is bounded, and
        // this is also what feeds related-company edges into the Entity graph) —
        // analysts shouldn't need to find a hidden "Load" button for this to work.
        function BusinessRelatedCompanies({ token, businessId, hasCompany }) {
            const [data, setData] = useState(null);
            const [loading, setLoading] = useState(false);
            const [err, setErr] = useState(null);

            const load = async () => {
                setLoading(true); setErr(null);
                try {
                    const d = await api(`/api/v1/businesses/${businessId}/ch-related-companies`, {}, token);
                    setData(d);
                } catch (e) { setErr(e.message); } finally { setLoading(false); }
            };

            useEffect(() => { if (hasCompany) load(); }, [businessId, hasCompany]);

            if (!hasCompany) return null;

            return (
              <div className="panel mb-6">
                <div className="panel-header flex justify-between items-center">
                  <div>
                    <div className="font-display text-xl gold">Director Network<HelpTip text="The other companies this company's current directors also sit on, found live from Companies House. Surfaces related entities and red flags — dissolved connected companies, directors spread across many companies, and companies sharing several directors with this one. Each red flag names the Companies House records behind it and carries a source-reliability grade (official register = A)." /></div>
                    <div className="text-xs text-muted font-mono mt-1">Related companies via shared directors · Companies House</div>
                  </div>
                  <button className="btn-ghost" onClick={load} disabled={loading}>{loading ? 'Loading…' : (data === null ? 'Load' : 'Reload')}</button>
                </div>
                <PanelHowTo id="business_related" />
                <div className="px-6 py-5">
                  {err && <div className="text-[var(--red)] text-sm mb-3">{err}</div>}
                  {data === null ? (
                    <div className="text-sm text-muted">{loading ? 'Loading…' : 'Click Load to map the other companies this company\'s current directors sit on, and surface due-diligence red flags. Live Companies House lookup.'}</div>
                  ) : (
                    <>
                      {data.cited_flags && data.cited_flags.length > 0 ? (() => {
                        // Warnings/high are red flags; info findings are context
                        // notes (group-internal, old or below-threshold) — shown
                        // separately so they never read as a red flag.
                        const renderFlag = (f, i) => {
                          const sev = f.severity === 'high' ? 'var(--red)' : f.severity === 'info' ? 'var(--text-soft)' : 'var(--amber)';
                          const ev = (f.evidence || []).map(e => e.company_name || e.officer).filter(Boolean);
                          return (
                            <div key={i} className="text-sm py-1" style={{ color: sev }}>
                              <span>{f.severity === 'info' ? 'ℹ' : '⚑'} {f.text}</span>
                              <span title={`Source: ${f.source} · reliability ${f.reliability} (${f.reliability_label}). Grade A = official statutory register.`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', marginLeft: '6px', fontSize: '9px', verticalAlign: 'middle' }}>
                                <span style={{ width: '13px', height: '13px', borderRadius: '3px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#0b0b0d', background: (GRADE_COLOR[f.reliability] || 'var(--text-muted)') }}>{f.reliability}</span>
                                <span style={{ color: 'var(--text-muted)' }}>{f.source}</span>
                              </span>
                              {ev.length > 0 && (
                                <span className="text-xs" style={{ color: 'var(--text-muted)' }}> — {ev.slice(0, 4).join(', ')}{ev.length > 4 ? ` +${ev.length - 4} more` : ''}</span>
                              )}
                            </div>
                          );
                        };
                        const red = data.cited_flags.filter(f => f.severity !== 'info');
                        const notes = data.cited_flags.filter(f => f.severity === 'info');
                        return (
                          <div className="mb-5">
                            {red.length > 0 && (
                              <>
                                <div className="text-xs uppercase tracking-widest font-mono mb-2" style={{ color: 'var(--amber)' }}>Red flags</div>
                                {red.map(renderFlag)}
                              </>
                            )}
                            {notes.length > 0 && (
                              <>
                                <div className={`text-xs uppercase tracking-widest text-muted font-mono mb-2${red.length > 0 ? ' mt-3' : ''}`}>Context notes</div>
                                {notes.map(renderFlag)}
                              </>
                            )}
                          </div>
                        );
                      })() : data.red_flags && data.red_flags.length > 0 && (
                        <div className="mb-5">
                          <div className="text-xs uppercase tracking-widest font-mono mb-2" style={{ color: 'var(--amber)' }}>Red flags</div>
                          {data.red_flags.map((f, i) => (
                            <div key={i} className="text-sm py-1" style={{ color: 'var(--amber)' }}>⚑ {f}</div>
                          ))}
                        </div>
                      )}
                      <div className="text-xs uppercase tracking-widest text-muted font-mono mb-2">
                        Related companies ({data.related_truncated ? `${data.related.length} of ${data.related_total} shown` : data.related.length}) · via {data.officers_considered} of {data.officers_total_current} current director{data.officers_total_current !== 1 ? 's' : ''}
                      </div>
                      {data.related.length === 0 ? (
                        <div className="text-sm text-muted">No related companies found through the current directors.</div>
                      ) : (
                        data.related.map((r, i) => (
                          <div key={i} className="table-row px-4 py-3" style={r.is_dissolved ? { borderLeft: '3px solid var(--red)' } : (r.is_new ? { borderLeft: '3px solid var(--gold)' } : {})}>
                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm">{r.company_name}
                                  {r.is_new && <span className="ml-2 text-[9px] font-mono uppercase tracking-widest" style={{ color: 'var(--gold)', border: '1px solid var(--gold)', borderRadius: '3px', padding: '1px 4px', verticalAlign: 'middle' }}>New</span>}
                                  {r.status && <span className="text-xs text-muted"> · {r.status}</span>}
                                  {r.company_number && <span className="text-xs text-muted font-mono"> · {r.company_number}</span>}
                                </div>
                                <div className="text-xs text-muted font-mono mt-1">via {(r.via || []).join(', ')}</div>
                              </div>
                              {r.shared_officers > 1 && <span className="font-mono text-xs" style={{ color: 'var(--gold)' }}>{r.shared_officers} shared</span>}
                            </div>
                          </div>
                        ))
                      )}
                      {(data.dropped && data.dropped.length > 0) && (
                        <div className="mt-4">
                          <div className="text-xs uppercase tracking-widest text-muted font-mono mb-2">No longer connected ({data.dropped.length})</div>
                          {data.dropped.map((d, i) => (
                            <div key={i} className="text-xs font-mono text-muted py-1" style={{ textDecoration: 'line-through' }}>
                              {d.company_name}{d.company_number ? ` · ${d.company_number}` : ''}
                            </div>
                          ))}
                        </div>
                      )}
                      {data.related_truncated && <div className="text-xs text-muted mt-3">Showing the {data.related.length} most-connected of {data.related_total} related companies. Flag counts are on all {data.related_total}.</div>}
                      {data.truncated && <div className="text-xs text-muted mt-3">Network mapped from the first {data.officers_considered} current directors.</div>}
                      {!data.had_prior_snapshot && <div className="text-xs text-muted mt-3">Baseline snapshot saved — future loads will flag what has changed.</div>}
                    </>
                  )}
                </div>
              </div>
            );
        }

export { BusinessRelatedCompanies };
